import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { FixedTermMarketContent } from '@/components/fixedTermMarket/fixedTermMarketContent'
import { FixedTermMarketHeader } from '@/components/fixedTermMarket/fixedTermMarketHeader'
import { FixedTermMarketSidePanel } from '@/components/fixedTermMarket/fixedTermMarketSidePanel'
import { Content, Page } from '@/components/layout/wrappers'
import { queryClient } from '@/config/query/queryClient'
import { ALL_SDK_NETWORKS } from '@/config/query/queryParams'
import { fixedTermMarketQueryOptions, useFixedTermMarketQuery } from '@/queries/fixedTermMarket.query'
import { getFixedTermMarketMeta } from '@/utils/meta/getFixedTermMarketMeta'
import { addressSchema } from '@vaultsfyi/common/schemas'
import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'
import { z } from 'zod'

const fixedTermMarketPageParamsSchema = z.object({
  network: z.enum(ALL_SDK_NETWORKS),
  vaultId: addressSchema,
})

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const parseResult = fixedTermMarketPageParamsSchema.safeParse(params)
  if (!parseResult.success) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const market = await queryClient.ensureQueryData(fixedTermMarketQueryOptions(parseResult.data)).catch(() => null)
  if (!market) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  return { market }
}

export type FixedTermMarketLoader = typeof loader

export const meta: MetaFunction<FixedTermMarketLoader> = ({ data, location }) =>
  getFixedTermMarketMeta({ market: data?.market, pathname: location.pathname })

export default function FixedTermMarket() {
  const { market: initialMarket } = useLoaderData<FixedTermMarketLoader>()
  const { data: market = initialMarket } = useFixedTermMarketQuery({
    network: initialMarket.network.name,
    vaultId: initialMarket.vaultId,
    initialData: initialMarket,
  })

  return (
    <Page>
      <ScrollArea className="h-full">
        <Content>
          <FixedTermMarketHeader market={market} />
          <div className="mt-4 flex flex-col lg:grid lg:grid-cols-[1fr_24rem] lg:gap-8">
            <div className="flex flex-col gap-4 overflow-hidden lg:grid lg:h-fit lg:max-w-full">
              <FixedTermMarketContent market={market} />
            </div>
            <FixedTermMarketSidePanel className="sticky top-6 hidden max-w-96 overflow-hidden lg:flex" />
          </div>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
